import { useEffect, useState } from 'react';
import { Platform } from 'react-native';

import { useUpdateOnboardingStateMutation } from '@graphql/generated';
import useMe from '@hooks/useMe';
import {
  clearInvitedSignup,
  isInvitedMember,
  rememberInvitedMember,
} from '@utils/invitedSignup';
import { logError } from '@utils/logNonFatalError';

// Owner-only onboarding steps an invited member must never be walked through.
// Mirrors web's updateOnboardingState1 (navigation/app-stack.web.tsx) so both
// platforms leave the same flags behind. Member steps (firstChatSent,
// firstTagUsed, ...) stay untouched so the member flow still runs in chat.
const OWNER_STEP_ATTRIBUTES = {
  welcomeModalSeen: true,
  projectCreationComplete: true,
  taskCreationComplete: true,
  inviteMemberComplete: true,
  checklistCreationComplete: true,
} as const;

/**
 * Resolves whether the signed-in user is a brand-new user who registered from
 * an invitation link (native only; web uses sessionStorage `invite_signup`).
 * `undefined` while the local marker is still being read, so callers can hold
 * onboarding UI instead of flashing it.
 */
export const useIsInvitedNewUser = (): boolean | undefined => {
  const { me } = useMe();
  const [invited, setInvited] = useState<boolean | undefined>(
    Platform.OS === 'web' ? false : undefined
  );

  useEffect(() => {
    if (Platform.OS === 'web') return;
    let cancelled = false;
    isInvitedMember(me?.id)
      .then((value) => !cancelled && setInvited(value))
      .catch(() => !cancelled && setInvited(false));
    return () => {
      cancelled = true;
    };
  }, [me?.id]);

  return invited;
};

/**
 * TA-10920 #18/#20: an invited new user follows the member (Carlos) flow, so
 * the owner steps (welcome modal, Create Project, Create Task, invite,
 * checklist) are marked complete once, server-side and in the cache.
 */
export const useSkipOwnerOnboardingForInvitedUser = (): void => {
  const { me } = useMe();
  const invitedNewUser = useIsInvitedNewUser();
  const [updateOnboardingState] = useUpdateOnboardingStateMutation();

  const ownerStepsPending =
    !!me?.onboardingState &&
    (
      Object.keys(OWNER_STEP_ATTRIBUTES) as Array<
        keyof typeof OWNER_STEP_ATTRIBUTES
      >
    ).some((key) => me.onboardingState?.[key] === false);

  // Persist the classification for this account before `new_user` goes away,
  // so owner onboarding surfaces stay hidden for the lifetime of the account.
  useEffect(() => {
    if (!me?.id || invitedNewUser === undefined) return;
    if (!invitedNewUser) {
      // An existing account that opened an invite link also leaves the signup
      // markers behind; they must not classify a later signup on this device.
      clearInvitedSignup().catch(() => {
        // Best-effort: markers alone never classify without new_user/registered.
      });
      return;
    }
    // Once bound to this account, drop the device-wide signup markers so the
    // next account signed up on this device isn't classified as invited.
    rememberInvitedMember(me.id)
      .then(clearInvitedSignup)
      .catch((error) => {
        logError({
          message: 'Failed to persist invited-member classification',
          errorMessage: error instanceof Error ? error.message : String(error),
          operationName: 'rememberInvitedMember',
        });
      });
  }, [invitedNewUser, me?.id]);

  useEffect(() => {
    if (!invitedNewUser || !me?.id || !ownerStepsPending) return;
    updateOnboardingState({
      variables: { attributes: OWNER_STEP_ATTRIBUTES },
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'User', id: me.id }),
          fields: {
            onboardingState: (existing) => ({
              ...existing,
              ...OWNER_STEP_ATTRIBUTES,
            }),
          },
        });
      },
    }).catch((error) => {
      // Best-effort: the effect re-runs on the next getMe if this fails.
      logError({
        message: 'Failed to skip owner onboarding for invited user',
        errorMessage: error instanceof Error ? error.message : String(error),
        operationName: 'updateOnboardingState',
      });
    });
  }, [invitedNewUser, me?.id, ownerStepsPending, updateOnboardingState]);
};
